import * as Yup from 'yup'
import { Button } from "@mui/material"
import { Alert } from "../../.."
import Field from "../fields/Field"
import { store } from "../../../stores/Store"
import { useFormik } from "formik"
import Help from '../../../misc/Help'
import { Icons } from '../../../misc/Icons'

const formatJson = (json: string): string => {
    return JSON.stringify(JSON.parse(json), null, "    ")
}

const jsonPlaceholder = '{\n    "key": "SWV_JA",\n    "version": "1.1",\n    "name": "My Questionnaire",\n    "pages": [\n        {\n            "title": "First Page",\n            "description": "<p>Enter your information here</p>",\n            "fields": [\n                {\n                    "name": "best_wrap_of_the_day"\n                    ...\n                    ...\n                    ...'

export default function TemplateForm ({
    template = {
        uid: "",
        key: "",
        version: "",
        displayName: "",
        advisorUid: "",
        json: "",
        active: true,
    },
    title = null,
    guidance = null,
    onCancel = () => store.AlertStore.Close(),
    onSubmit,
}) {

    const { assignPageIDs } = store.QuestionnaireStore

    const advisorOptions = [{value: "GLOBAL", label: "Global (Available for all accounts)"}]

    store.NodeStore.accounts.filter(a => !!a.advisorUid).forEach((account) => {
        advisorOptions.push({value: account.advisorUid, label: account.accountName})
    })

    var formik = useFormik({
        initialValues: {
            advisorUid: template.advisorUid,
            json: template.json ? template.json : "",
            active: template.active,
        },

        validationSchema: Yup.object({
            json: Yup.string().test("json", "Invalid JSON format", (value) => {
                try {
                    JSON.parse(value)
                    return true;
                } catch (error) {
                    return false;
                }
            }).test("json", "Please define 'key' in the JSON", (value) => {
                try {
                    return !!JSON.parse(value)?.key
                } catch (e) {
                    return false
                }
            }).test("json", "Please define 'version' in the JSON", (value) => {
                try {
                    return !!JSON.parse(value)?.version
                } catch (e) {
                    return false
                }
            }).test("json", "Please define 'name' in the JSON", (value) => {
                try {
                    return !!JSON.parse(value)?.name
                } catch (e) {
                    return false
                }
            }).test("json", "Please define 'pages' in the JSON", (value) => {
                try {
                    return !!JSON.parse(value)?.pages
                } catch (e) {
                    return false
                }
            })
        }),

        onSubmit: async (values, actions) => {
            
            var key = getJsonAttribute("key")
            var version = getJsonAttribute("version")
            var name = getJsonAttribute("name")
            var json = formik.values.json

            // if (json !== template.json) {
            //     // Only update page IDs if the json has been changed
            //     await assignPageIDs(json).then(updateJson => {
            //         json = updateJson
            //         formik.setFieldValue("json", json)
            //     }).catch(() => {
            //         json = template.json
            //         Alert({message: "Invalid JSON"})
            //     })
            // }

            await onSubmit({
                ...template, 
                ...values, 
                json: json,
                key: key,
                version: version,
                displayName: name
            }).catch(() => {
                Alert({message: "Failed to update Template"})
            })
        }
    })

    const getJsonAttribute = (key) => {
        try {
            var json = JSON.parse(formik.values.json)
            return json[key]
        } catch (e) {
            return false
        }
    }

    var key = getJsonAttribute("key")
    var version = getJsonAttribute("version")
    var name = getJsonAttribute("name")

    var nonstandardKey = !store.QuestionnaireStore.templates.find((t => t.key === key))

    const KeyWarning = () => {
        return (
            <span>
                <Help content={<span className="warning"> - This key has not been used before <Icons.Info style={{fontSize: "12px"}} /></span>}>
                    <p>You are creating a Template with a non-standard key, are you sure you want to do this?</p>
                    <p>Standard keys are:</p>
                    {store.AppStore.DisplayDictionary("template_key", true).map((option, i) => (
                        <div key={i} style={{display: "grid", gridTemplateColumns: "150px auto", margin: "4px 0 4px 4px"}}>
                            <span>{option.value}</span>
                            <span>{option.label}</span>
                        </div>
                    ))}
                </Help>
            </span>
        )
    }

    return (
        <form className="form questionnaire-template-detail" onSubmit={formik.handleSubmit}>
            <div className="header">
                {title ? <h2>{title}</h2> : null}

                {guidance ? <h2>{guidance}</h2> : null}

                <Field.Switch
                    formik
                    label="Active"
                    name="active"
                    warning={false}
                    value={formik.values.active}
                    onChange={formik.handleChange} />
            </div>

            <div>
                <div className="form-fields">
                    <div style={{display: "grid", gridTemplateColumns: "80px auto"}}>
                        <span className="font-grey">Key:</span>       <span>{key ? key : "-"} {key && nonstandardKey ? <KeyWarning /> : null}</span>
                        <span className="font-grey">Version:</span>   <span>{version ? version : "-"}</span>
                        <span className="font-grey">Name:</span>      <span>{name ? name : "-"}</span>
                    </div>

                    <Field.Select
                        formik
                        label="Exclusive access"
                        name="advisorUid"
                        value={formik.values.advisorUid}
                        onChange={formik.handleChange}
                        warning={formik.submitCount && formik.errors.advisorUid ? formik.errors.advisorUid : false}
                        options={advisorOptions}
                        helpText="If an account is selected, this template will only be available for this account. Otherwise, the template will be visible to all accounts on this instance." />
                </div>

                <div style={{display: "grid"}}>
                    <textarea
                        name="json"
                        placeholder={jsonPlaceholder}
                        value={formik.values.json}
                        rows={18}
                        onChange={formik.handleChange} />

                    {formik.submitCount && formik.errors.json ? (
                        <div className="warning">{formik.errors.json}</div>
                    ) : null}
                </div>
            </div>

            <div className="form-buttons">
                <Button 
                    className="btn btn-sm" 
                    onClick={() => onCancel()}>
                    Cancel
                </Button>

                <div className="form-buttons">
                    <Button
                        variant="contained"
                        className="btn btn-sm"
                        onClick={() => {
                            assignPageIDs(formik.values.json).then(templateJson => {
                                formik.setFieldValue("json", templateJson)
                            }).catch(() => {
                                Alert({message: "Invalid JSON"})
                            })
                        }}>
                        Assign page IDs
                    </Button>

                    <Button
                        color="success"
                        variant="contained"
                        className="btn btn-sm"
                        type="submit"
                        disabled={formik.isSubmitting}>
                        Submit
                    </Button>
                </div>
            </div>
        </form>
    )
}